import * as React from "react";
import { Chip } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FunnelSimpleIcon } from "@phosphor-icons/react/dist/ssr/FunnelSimple";
import { usePopover } from "@/hooks/use-popover";

import { Option } from "../customTable/types";
import { XCircleIcon } from "@phosphor-icons/react";

function noop(..._: unknown[]): void {
	// Do nothing
}

export interface FilterContextValue<T = unknown> {
	anchorEl: HTMLElement | null;
	onApply: (value: unknown) => void;
	onClose: () => void;
	open: boolean;
	value?: T;
}

export const FilterContext = React.createContext<FilterContextValue>({
	anchorEl: null,
	onApply: noop,
	onClose: noop,
	open: false,
	value: undefined,
});

export function useFilterContext(): FilterContextValue {
	const context = React.useContext(FilterContext);

	if (!context) {
		throw new Error("useFilterContext must be used within a FilterProvider");
	}

	return context;
}

export interface FilterButtonProps {
	displayValue?: string | string[] | { label: string; value: string }[];
	label: string;
	onFilterApply?: (value: unknown) => void;
	onFilterDelete?: () => void;
	onChipDelete?: (value: string) => void;
	popover: React.ReactNode;
	value?: string | string[] | Option[] | Option | { from?: string; to?: string };
}

// Currently, the `value` prop can be string | number | boolean | undefined
export function FilterButton({
	displayValue,
	label,
	onFilterApply,
	onFilterDelete,
	popover,
	value,
	onChipDelete,
}: FilterButtonProps): React.JSX.Element {
	const { anchorRef, handleOpen, handleClose, open } = usePopover<HTMLButtonElement>();

	const handleApply = React.useCallback(
		(newValue: unknown) => {
			handleClose();
			onFilterApply?.(newValue);
		},
		[handleClose, onFilterApply]
	);

	return (
		<FilterContext.Provider
			value={{ anchorEl: anchorRef.current, onApply: handleApply, onClose: handleClose, open, value }}
		>
			<Button
				color="secondary"
				onClick={handleOpen}
				ref={anchorRef}
				startIcon={
					(Array.isArray(value) && value?.length) || (value as Option)?.value || (value as string)?.length > 0 ? (
						<Box
							onClick={(event) => {
								event.stopPropagation();
								event.preventDefault();
								onFilterDelete?.();
							}}
							onKeyUp={(event) => {
								event.stopPropagation();
								event.preventDefault();

								if (event.key === "Enter" || event.key === " ") {
									onFilterDelete?.();
								}
							}}
							role="button"
							sx={{ display: "flex", alignItems: "center" }}
							tabIndex={0}
						>
							<XCircleIcon size={20} />
						</Box>
					) : (
						<FunnelSimpleIcon size={16} />
					)
				}
				variant="outlined"
				sx={{
					borderColor: (Array.isArray(value) && value?.length) || (value as Option)?.value || (value as string)?.length > 0 
						? "var(--mui-palette-primary-main)" 
						: "var(--mui-palette-divider)",
					color: (Array.isArray(value) && value?.length) || (value as Option)?.value || (value as string)?.length > 0 
						? "var(--mui-palette-primary-main)" 
						: "var(--mui-palette-text-secondary)",
				}}
			>
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					{label}
					{Array.isArray(displayValue) && displayValue.length > 0 ? (
						<Box sx={{ display: "flex", gap: 1 }}>
							{displayValue.map((v) => (
								<Chip
									color="primary"
									size="small"
									key={v.value + v.label}
									label={v.label}
									onDelete={() => {
										onChipDelete?.(v.value);
									}}
								/>
							))}
						</Box>
					) : typeof displayValue === "string" && displayValue ? (
						<React.Fragment>
							:{" "}
							<Box component="span" sx={{ color: "var(--mui-palette-primary-main)" }}>
								{displayValue}
							</Box>
						</React.Fragment>
					) : null}
				</Box>
			</Button>
			{popover}
		</FilterContext.Provider>
	);
}

interface FilterPopoverProps {
	anchorEl: HTMLElement | null;
	children: React.ReactNode;
	onClose: () => void;
	open: boolean;
	title: string;
}

export function FilterPopover({ children, title, onClose, anchorEl, open }: FilterPopoverProps): React.JSX.Element {
	return (
		<Popover
			anchorEl={anchorEl}
			anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
			onClose={onClose}
			open={Boolean(anchorEl && open)}
			sx={{ 
				"& .MuiPopover-paper": { 
					mt: "4px", 
					width: "320px",
					borderRadius: "8px",
					boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)"
				} 
			}}
		>
			<Stack spacing={2} sx={{ p: 2 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<FunnelSimpleIcon size={20} />
					<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
						{title}
					</Typography>
				</Box>
				{children}
			</Stack>
		</Popover>
	);
}
